import type {
	ProcessKind,
	ProcessSpec,
	ProcessSpecWrite,
} from '../../interfaces/ProcessCatalog.Interface';
import { api } from '../slices/api';

/** The catalog itself, plus the one global batching number that goes with it.
 *
 * Reads are open to everyone (the line item pickers and the tag printer both
 * need them); writes are Shop-Manager-only on the server, since making a row a
 * special process starts printing tags on every job that matches it.
 */
export const processCatalogApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getProcessSpecs: builder.query<
			ProcessSpec[],
			{ kind?: ProcessKind; is_active?: boolean } | void
		>({
			query: (params) => ({
				url: 'orders/process-specs/',
				params: params ?? undefined,
			}),
			providesTags: ['ProcessSpecs'],
		}),
		createProcessSpec: builder.mutation<ProcessSpec, ProcessSpecWrite>({
			query: (body) => ({
				url: 'orders/process-specs/',
				method: 'POST',
				body,
			}),
			// A new row can become the default or start matching a finish, both of
			// which change what every line item resolves to.
			invalidatesTags: ['ProcessSpecs', 'OrderLineItem', 'Order'],
		}),
		updateProcessSpec: builder.mutation<
			ProcessSpec,
			{ id: string; body: ProcessSpecWrite }
		>({
			query: ({ id, body }) => ({
				url: `orders/process-specs/${id}/`,
				method: 'PATCH',
				body,
			}),
			invalidatesTags: ['ProcessSpecs', 'OrderLineItem', 'Order'],
		}),
		deleteProcessSpec: builder.mutation<void, string>({
			query: (id) => ({
				url: `orders/process-specs/${id}/`,
				method: 'DELETE',
			}),
			invalidatesTags: ['ProcessSpecs', 'OrderLineItem', 'Order'],
		}),
		getProcessMinBatch: builder.query<{ min_batch: number }, void>({
			query: () => 'orders/process-specs/min-batch/',
			providesTags: ['ProcessMinBatch'],
		}),
		setProcessMinBatch: builder.mutation<{ min_batch: number }, number>({
			query: (min_batch) => ({
				url: 'orders/process-specs/min-batch/',
				method: 'PATCH',
				body: { min_batch },
			}),
			// Changes the batch count of every batched process, so every resolved
			// line item payload is now stale.
			invalidatesTags: ['ProcessMinBatch', 'OrderLineItem', 'Order'],
		}),
	}),
});

export const {
	useGetProcessSpecsQuery,
	useCreateProcessSpecMutation,
	useUpdateProcessSpecMutation,
	useDeleteProcessSpecMutation,
	useGetProcessMinBatchQuery,
	useSetProcessMinBatchMutation,
} = processCatalogApi;
